import { Badge, Check, Tooltip, X } from '@chain-zen/ui'

import type { IPhoneNumberInfo } from '../../types/user'

/**
 * Get the phone type info
 * @param isValid is the phone number valid
 * @param carrierType is the carrier type
 * @returns
 */
const getPhoneTypeInfo = (isValid: boolean, carrierType: string | undefined) => {
  if (!isValid) {
    return {
      emoji: '❌',
      icon: X,
      label: 'Invalid',
      variant: 'destructive' as const
    }
  }

  switch (carrierType?.toLowerCase()) {
    case 'mobile':
      return {
        emoji: '📱',
        icon: Check,
        label: 'Mobile',
        variant: 'default' as const
      }
    case 'landline':
    case 'fixed_line':
      return {
        emoji: '☎️',
        icon: Check,
        label: 'Landline',
        variant: 'secondary' as const
      }
    case 'voip':
      return {
        emoji: '💻',
        icon: Check,
        label: 'VoIP',
        variant: 'secondary' as const
      }
    default:
      return {
        emoji: '📞',
        icon: Check,
        label: carrierType || 'Phone',
        variant: 'secondary' as const
      }
  }
}

export const PhoneInfoBadge = ({
  phoneNumberInfo,
  showHealthy = true
}: {
  phoneNumberInfo: IPhoneNumberInfo
  showHealthy?: boolean
}) => {
  const { carrierTypeDescription, isValid, carrierName, carrierType } = phoneNumberInfo

  // Get phone type info with emoji and label
  const phoneTypeInfo = getPhoneTypeInfo(isValid, carrierType)
  const isValidMobile = isValid && carrierType === 'mobile'
  const validityLabel = isValidMobile ? 'Valid' : 'Not Valid'
  const shouldShowBadges = showHealthy ? true : !isValidMobile

  if (!shouldShowBadges) {
    return null
  }

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="phone-info-badge">
      {/* Phone type badge with meaningful tooltip */}
      <Tooltip
        content={`${phoneTypeInfo.label} phone number${carrierTypeDescription ? ` - ${carrierTypeDescription}` : ''}`}
      >
        <Badge variant={isValidMobile ? 'success' : 'danger'} size="xs" className="flex items-center gap-1">
          {isValidMobile ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
          {validityLabel}
        </Badge>
      </Tooltip>

      {/* Carrier name (if present) - natural size with wrapping */}
      {!!carrierName && (
        <Tooltip content={`Phone service provider: ${carrierName}`}>
          <Badge variant="info" size="xs" className="truncate max-w-24 sm:max-w-none">
            {carrierName}
          </Badge>
        </Tooltip>
      )}
    </div>
  )
}
